import { useState, useMemo, useEffect } from 'react';
import { Pencil, Loader2, Check, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, HOVER_REVEAL } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useLocations } from '@/hooks/useLocations';
import { LocationFormDialog } from '@/components/shared/LocationFormDialog';
import {
  formatLocationDisplay,
  getItemLabel,
  groupLocations,
  type LocationGroup,
} from '@/components/shared/location-utils';
import type { Location } from '@jobos/shared/types/location';

interface InlineLocationSelectProps {
  clientId: string;
  locationId: string;
  displayContent: React.ReactNode;
  onSave: (locationId: string) => Promise<void> | void;
}

export function InlineLocationSelect({
  clientId,
  locationId,
  displayContent,
  onSave,
}: InlineLocationSelectProps) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogDefaults, setAddDialogDefaults] = useState<{
    managerId?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null>(null);

  const { data: clientLocations = [] } = useLocations(clientId);
  const groups = useMemo(() => groupLocations(clientLocations), [clientLocations]);

  const isSearching = search.trim().length > 0;

  // When popover opens, expand only the group containing the current location
  useEffect(() => {
    if (!open) return;
    const selectedGroup = groups.find((g) =>
      g.locations.some((l) => l.id === locationId)
    );
    setExpandedGroups(selectedGroup ? new Set([selectedGroup.key]) : new Set());
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelect = async (newLocationId: string) => {
    setOpen(false);
    if (newLocationId !== locationId) {
      setIsSaving(true);
      try {
        await onSave(newLocationId);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleAddUnit = (group: LocationGroup) => {
    const template = group.locations[0]!;
    setAddDialogDefaults({
      addressLine1: template.addressLine1 || '',
      city: template.city || '',
      state: template.state || '',
      zip: template.zip || '',
      managerId: template.managerId || '',
    });
    setOpen(false);
    setAddDialogOpen(true);
  };

  const handleAddNew = () => {
    setAddDialogDefaults(null);
    setOpen(false);
    setAddDialogOpen(true);
  };

  const handleLocationCreated = async (location: Location) => {
    setAddDialogOpen(false);
    setIsSaving(true);
    try {
      await onSave(location.id);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="group flex items-start justify-between">
            <div className="text-sm text-gray-900 flex items-start gap-2">
              <div>{displayContent}</div>
              {isSaving && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {!isSaving && (
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Change service address"
                  className={cn('h-9 shrink-0 px-3 sm:h-7 sm:px-2', HOVER_REVEAL)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent className="w-80 max-w-[calc(100vw-2rem)] p-0" align="start">
          <Command
            filter={(value: string, search: string) => {
              if (value.toLowerCase().includes(search.toLowerCase())) return 1;
              return 0;
            }}
          >
            <CommandInput
              placeholder="Search locations..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isSearching ? (
                <>
                  <CommandEmpty>No locations found.</CommandEmpty>
                  {groups.map((group) => (
                    <CommandGroup key={group.key} heading={group.heading}>
                      {group.locations.map((location) => (
                        <CommandItem
                          key={location.id}
                          value={formatLocationDisplay(location)}
                          onSelect={() => handleSelect(location.id)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              locationId === location.id
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          {getItemLabel(location)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </>
              ) : (
                groups.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);
                  return (
                    <div key={group.key} className="px-1 py-0.5">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        <ChevronRight
                          className={cn(
                            'h-3 w-3 shrink-0 transition-transform',
                            isExpanded && 'rotate-90'
                          )}
                        />
                        <span className="flex-1 text-left truncate">
                          {group.heading}
                        </span>
                        <span className="text-muted-foreground/60">
                          ({group.locations.length})
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="ml-2">
                          {group.locations.map((location) => (
                            <CommandItem
                              key={location.id}
                              value={formatLocationDisplay(location)}
                              onSelect={() => handleSelect(location.id)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4 shrink-0',
                                  locationId === location.id
                                    ? 'opacity-100'
                                    : 'opacity-0'
                                )}
                              />
                              {getItemLabel(location)}
                            </CommandItem>
                          ))}
                          <button
                            type="button"
                            onClick={() => handleAddUnit(group)}
                            className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            <Plus className="h-3 w-3" />
                            Add unit here
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CommandList>
            {!isSearching && (
              <div className="border-t p-1">
                <button
                  type="button"
                  onClick={handleAddNew}
                  className="w-full flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                >
                  <Plus className="h-3 w-3" />
                  Add new location
                </button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      <LocationFormDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleLocationCreated}
        clientId={clientId}
        defaultValues={addDialogDefaults}
      />
    </>
  );
}
